import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X,
  AlertTriangle,
  Phone,
  Shield,
  Droplet,
  Home,
  Radio,
  Heart,
  Clock
} from 'lucide-react'
import '../styles/emergency.css'

/**
 * EmergencyMode Component
 * Quick access to critical survival information without gamification
 * Designed for actual emergency situations
 */
const EmergencyMode = () => {
  const navigate = useNavigate()
  const [activeSection, setActiveSection] = useState('immediate')

  // Critical emergency information organized by timeframe
  const emergencyInfo = {
    immediate: {
      title: "IMMEDIATE ACTIONS (0-15 minutes)",
      icon: AlertTriangle,
      color: '#ef4444',
      steps: [
        "GET INSIDE - Find nearest building, preferably brick/concrete",
        "GO TO CENTER - Move away from windows, get to basement if possible",
        "STAY INSIDE - Do not go outside for any reason",
        "TURN OFF VENTILATION - Close all windows, doors, shut down HVAC",
        "LISTEN FOR INFO - Use battery radio, phone, or emergency broadcast"
      ]
    },
    first48: {
      title: "FIRST 48 HOURS",
      icon: Clock,
      color: '#f59e0b',
      steps: [
        "STAY SHELTERED - Radiation is highest now, decreasing rapidly",
        "SEAL YOUR ROOM - Use duct tape and plastic on windows/doors",
        "CONSERVE SUPPLIES - Ration water (1 gal/day) and food",
        "MONITOR RADIO - Listen for official instructions only",
        "DECONTAMINATE - Remove outer clothes, seal in plastic, shower if possible"
      ]
    },
    shelter: {
      title: "SHELTER ESSENTIALS",
      icon: Home,
      color: '#3b82f6',
      steps: [
        "BEST: Underground areas - basements, subways, tunnels",
        "GOOD: Center of large buildings, away from roof/walls",
        "AVOID: Cars, mobile homes, wooden structures",
        "SHIELDING: Every wall/floor between you and outside helps",
        "TIME: Plan to shelter minimum 24-48 hours"
      ]
    },
    health: {
      title: "HEALTH & SAFETY",
      icon: Heart,
      color: '#10b981',
      steps: [
        "RADIATION SICKNESS: Nausea, fatigue, hair loss (appears days later)",
        "BURNS: Cool with water, cover with clean cloth, don't break blisters",
        "WOUNDS: Apply pressure, elevate, seek help when safe",
        "MEDICATIONS: Take prescribed meds, potassium iodide if directed",
        "CONTAMINATION: Remove clothes, shower, don't scrub hard"
      ]
    },
    supplies: {
      title: "CRITICAL SUPPLIES",
      icon: Droplet,
      color: '#0891b2',
      steps: [
        "WATER: 1 gallon/person/day - fill containers NOW if time",
        "FOOD: Non-perishables that don't need cooking",
        "FIRST AID: Bandages, medications, antiseptic",
        "TOOLS: Radio, flashlight, batteries, duct tape, plastic",
        "SANITATION: Bags, bucket, bleach, toilet paper"
      ]
    },
    communication: {
      title: "COMMUNICATION",
      icon: Radio,
      color: '#8b5cf6',
      steps: [
        "TEXT, DON'T CALL - Texts use less bandwidth",
        "EMERGENCY BROADCAST - Find local station frequency",
        "SAVE BATTERY - Turn off WiFi, Bluetooth, lower brightness",
        "ONE CONTACT - Pick out-of-state relative as central contact",
        "OFFICIAL ONLY - Ignore social media rumors"
      ]
    }
  }

  // Emergency contacts (would be user-defined in production)
  const emergencyContacts = [
    { name: "Emergency Services", number: "911", primary: true },
    { name: "Poison Control", number: "1-800-222-1222", primary: false },
    { name: "FEMA", number: "1-800-621-3362", primary: false }
  ]

  return (
    <div className="emergency-shell">
      <div className="landing-ambient emergency-ambient" />

      {/* Header */}
      <motion.header
        className="emergency-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="container">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold flex items-center gap-sm">
              <AlertTriangle size={28} />
              EMERGENCY MODE
            </h1>
            <button
              className="close-button"
              onClick={() => navigate('/')}
              aria-label="Exit emergency mode"
            >
              <X size={24} />
            </button>
          </div>
        </div>
      </motion.header>

      {/* Emergency Navigation */}
      <nav className="emergency-nav">
        <div className="container">
          <div className="nav-buttons">
            {Object.keys(emergencyInfo).map((key, index) => (
              <motion.button
                key={key}
                className={`nav-button ${activeSection === key ? 'active' : ''}`}
                onClick={() => setActiveSection(key)}
                style={{
                  '--button-color': emergencyInfo[key].color,
                  borderColor: activeSection === key ? emergencyInfo[key].color : 'transparent'
                }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.05 }}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                {React.createElement(emergencyInfo[key].icon, { size: 20 })}
                <span className="nav-label">{key.toUpperCase()}</span>
              </motion.button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="emergency-content container">
        {/* Active Section */}
        <AnimatePresence mode="wait">
          <motion.section
            key={activeSection}
            className="info-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <h2
              className="section-title"
              style={{ color: emergencyInfo[activeSection].color }}
            >
              {emergencyInfo[activeSection].title}
            </h2>
            <ol className="emergency-steps">
              {emergencyInfo[activeSection].steps.map((step, index) => (
                <motion.li
                  key={index}
                  className="emergency-step"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <span className="step-number">{index + 1}</span>
                  <span className="step-text">{step}</span>
                </motion.li>
              ))}
            </ol>
          </motion.section>
        </AnimatePresence>

        {/* Quick Reference Card */}
        <motion.section
          className="quick-reference"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h3 className="font-bold mb-md">REMEMBER THE BASICS</h3>
          <div className="reference-grid">
            {[
              { icon: Shield, text: 'GET INSIDE' },
              { icon: Home, text: 'STAY INSIDE' },
              { icon: Radio, text: 'STAY TUNED' }
            ].map((item, index) => (
              <motion.div
                key={item.text}
                className="reference-item"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 + index * 0.1 }}
              >
                <item.icon size={24} />
                <span>{item.text}</span>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Emergency Contacts */}
        <motion.section
          className="emergency-contacts"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h3 className="font-bold mb-md">EMERGENCY CONTACTS</h3>
          <div className="contacts-list">
            {emergencyContacts.map((contact, index) => (
              <motion.a
                key={index}
                href={`tel:${contact.number}`}
                className="contact-item"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + index * 0.05 }}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                <div className="flex items-center gap-sm">
                  <Phone size={20} />
                  <div>
                    <div className="font-medium">{contact.name}</div>
                    <div className="text-sm">{contact.number}</div>
                  </div>
                </div>
                {contact.primary && (
                  <span className="primary-badge">PRIMARY</span>
                )}
              </motion.a>
            ))}
          </div>
        </motion.section>

        {/* Critical Timer */}
        <motion.section
          className="timer-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <h3 className="font-bold mb-md">RADIATION DECAY TIMELINE</h3>
          <div className="timeline">
            {[
              { time: '1 HOUR', reduction: '50% reduction' },
              { time: '7 HOURS', reduction: '90% reduction' },
              { time: '48 HOURS', reduction: '99% reduction' }
            ].map((item, index) => (
              <motion.div
                key={item.time}
                className="timeline-item"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 + index * 0.1 }}
              >
                <span className="time">{item.time}</span>
                <span className="radiation">{item.reduction}</span>
              </motion.div>
            ))}
          </div>
        </motion.section>
      </main>
    </div>
  )
}

export default EmergencyMode
